import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, Download, Award, ClipboardList, AlertTriangle } from "lucide-react";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import { gleichstaendeDerKategorie, rankingHatPlaetze } from "../utils/gleichstand";
import { mitSitzungspruefung } from "../utils/fetchWithAuth";

/** Heutiges Datum als YYYY-MM-DD in LOKALER Zeit.
 *  toISOString() waere UTC und wuerde in der deutschen Sommerzeit zwischen
 *  Mitternacht und 2 Uhr den Vortag vorbelegen. */
function heuteIso() {
  const d = new Date();
  const zweistellig = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${zweistellig(d.getMonth() + 1)}-${zweistellig(d.getDate())}`;
}

export default function Exporte() {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem("token");

  // === Urkunden ===
  const [urkunde, setUrkunde] = useState({
    ort: "Ergoldsbach",
    datum: heuteIso(),
    unterschrift: "",
  });
  const [loadingUrkunden, setLoadingUrkunden] = useState(false);
  const [urkundenHinweis, setUrkundenHinweis] = useState(null);

  // Gleichstaende VOR dem Druck zeigen: eine gedruckte Urkunde laesst sich
  // nicht mehr korrigieren. Quelle ist das Ranking mit den zentral vergebenen
  // Plaetzen – dieselben, die auf den Urkunden landen.
  const navigate = useNavigate();
  const [rankingTeams, setRankingTeams] = useState(null);
  useEffect(() => {
    fetchWithAuth(`${BACKEND_URL}/results`, navigate)
      .then((data) => setRankingTeams(Array.isArray(data?.teams) ? data.teams : []))
      .catch(() => setRankingTeams(null));
  }, [BACKEND_URL, navigate]);
  const gleichstaendeKinder = rankingTeams ? gleichstaendeDerKategorie(rankingTeams, "Kinder") : [];
  const plaetzeFehlen = rankingTeams ? !rankingHatPlaetze(rankingTeams) : false;

  // === Team-Zettel fuers Klemmbrett ===
  const [loadingZettel, setLoadingZettel] = useState(null); // "Kinder" | "Erwachsene" | "alle"
  const [zettelHinweis, setZettelHinweis] = useState(null);
  // "a5": ein Zettel je Blatt; "a4": zwei Teams je Querbogen zum Durchschneiden.
  const [zettelFormat, setZettelFormat] = useState("a5");

  // === Dateien herunterladen ===
  // Das Erstellen dauert bei grossen Datenmengen einige Sekunden. Ohne
  // sichtbaren Zustand wirkt der Klick wirkungslos und wird wiederholt.
  const [ladeDatei, setLadeDatei] = useState(null); // Pfad der laufenden Erstellung
  const [dateiHinweis, setDateiHinweis] = useState(null); // { pfad, art, text }

  const downloadFile = async (path, filename, errorMsg) => {
    setLadeDatei(path);
    setDateiHinweis(null);
    try {
      const res = mitSitzungspruefung(await fetch(`${BACKEND_URL}${path}`, {
        headers: { Authorization: `Bearer ${token}` },
      }));
      if (!res.ok) throw new Error(errorMsg);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
      setDateiHinweis({ pfad: path, art: "ok", text: "PDF erstellt – der Download startet." });
    } catch (err) {
      console.error(err);
      setDateiHinweis({ pfad: path, art: "fehler", text: err.message || String(err) });
    } finally {
      setLadeDatei(null);
    }
  };

  /** Download-Button mit Ladezustand; sperrt waehrend einer laufenden Erstellung. */
  const dateiButton = ({ pfad, dateiname, fehlertext, label, className }) => (
    <button
      disabled={ladeDatei !== null}
      aria-busy={ladeDatei === pfad}
      onClick={() => downloadFile(pfad, dateiname, fehlertext)}
      className={`${className} flex items-center justify-center gap-2 disabled:opacity-60`}
    >
      <Download size={18} />
      {ladeDatei === pfad ? "wird erstellt…" : label}
    </button>
  );

  const dateiMeldung = (pfad) =>
    dateiHinweis && dateiHinweis.pfad === pfad ? (
      <p
        className={`text-sm mt-3 ${
          dateiHinweis.art === "fehler" ? "text-red-400" : "text-green-400"
        }`}
      >
        {dateiHinweis.art === "fehler" ? "❌ " : "✅ "}
        {dateiHinweis.text}
      </p>
    ) : null;

  // === Urkunden herunterladen ===
  //
  // Eigener Download-Pfad statt `downloadFile`: dieser Export kann aus einem
  // ganz normalen Bediengrund fehlschlagen (noch keine Kinder-Teams mit
  // Teilnehmernamen) und liefert Rueckmeldungen ueber Header mit. Ein blosses
  // `alert("Fehler beim Download")` wuerde die eigentliche Ursache verschlucken.
  const downloadUrkunden = async () => {
    setLoadingUrkunden(true);
    setUrkundenHinweis(null);
    try {
      const params = new URLSearchParams({
        ort: urkunde.ort,
        datum: urkunde.datum,
        unterschrift: urkunde.unterschrift,
      });

      const res = mitSitzungspruefung(await fetch(
        `${BACKEND_URL}/pdf/export-urkunden-kinder?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      ));

      if (!res.ok) {
        let text = "Urkunden konnten nicht erstellt werden.";
        try {
          const data = await res.json();
          if (data && data.error) text = data.error;
        } catch {
          // Antwort war kein JSON – dann bleibt der allgemeine Text stehen.
        }
        throw new Error(text);
      }

      const anzahl = res.headers.get("X-Urkunden-Anzahl");
      const ohneRoh = res.headers.get("X-Urkunden-Ohne-Mitglieder");
      const ohne = ohneRoh ? decodeURIComponent(ohneRoh) : null;

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Kirtagames_urkunden_kinder.pdf";
      a.click();
      window.URL.revokeObjectURL(url);

      setUrkundenHinweis({
        art: ohne ? "warnung" : "ok",
        text: ohne
          ? `${anzahl} Urkunden erstellt. Ohne Urkunde geblieben sind Teams ohne ` +
            `eingetragene Teilnehmernamen: ${ohne}`
          : `${anzahl} Urkunden erstellt – Platz 1 liegt vorne.`,
      });
    } catch (err) {
      console.error(err);
      setUrkundenHinweis({ art: "fehler", text: err.message || String(err) });
    } finally {
      setLoadingUrkunden(false);
    }
  };

  // === Team-Zettel herunterladen ===
  //
  // Eigener Pfad wie bei den Urkunden: der Export meldet ueber Header, wie
  // viele Zettel entstanden sind und welche Teams uebersprungen wurden. Ein
  // uebersprungenes Team faellt sonst erst beim Verteilen auf – und steht dann
  // ohne Zettel und ohne QR-Code da.
  const downloadZettel = async (kategorie) => {
    const schluessel = kategorie || "alle";
    setLoadingZettel(schluessel);
    setZettelHinweis(null);
    try {
      const a4 = zettelFormat === "a4";
      const pfad = kategorie
        ? `/pdf/export-team-zettel?kategorie=${encodeURIComponent(kategorie)}&format=${zettelFormat}`
        : `/pdf/export-team-zettel?format=${zettelFormat}`;

      const res = mitSitzungspruefung(await fetch(`${BACKEND_URL}${pfad}`, {
        headers: { Authorization: `Bearer ${token}` },
      }));

      if (!res.ok) {
        let text = "Team-Zettel konnten nicht erstellt werden.";
        try {
          const data = await res.json();
          if (data && data.error) text = data.error;
        } catch {
          // Antwort war kein JSON – der allgemeine Text bleibt stehen.
        }
        throw new Error(text);
      }

      const anzahl = res.headers.get("X-Zettel-Anzahl");
      const ohneRoh = res.headers.get("X-Zettel-Ohne-Token");
      const ohne = ohneRoh ? decodeURIComponent(ohneRoh) : null;
      const zuLangRoh = res.headers.get("X-Zettel-Zu-Lang");
      const zuLang = zuLangRoh ? decodeURIComponent(zuLangRoh) : null;

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const suffix = a4 ? "_a4" : "";
      a.download = kategorie
        ? `Kirtagames_teamzettel_${kategorie.toLowerCase()}${suffix}.pdf`
        : `Kirtagames_teamzettel${suffix}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);

      // Beide Warnungen koennen gleichzeitig auftreten und haben verschiedene
      // Ursachen – deshalb aneinandergehaengt statt einander verdraengend.
      const warnungen = [];
      if (ohne) {
        warnungen.push(
          `Ohne Zettel geblieben sind Teams ohne Zugangsschlüssel: ${ohne}. ` +
            `Einmal in der Team-Verwaltung speichern – der Schlüssel wird dabei nachvergeben.`
        );
      }
      if (zuLang) {
        warnungen.push(
          a4
            ? `Bei diesen Teams passt die Spielliste nicht mehr auf die Vorderseite: ` +
                `${zuLang}. Die unteren Zeilen sind abgeschnitten – Stationen ` +
                `zusammenlegen oder Bereiche kürzen und neu erstellen.`
            : `Bei diesen Teams passt die Spielliste nicht mehr auf die A5-Vorderseite: ` +
                `${zuLang}. Sie bekommen drei statt zwei Seiten, dadurch verrutscht der ` +
                `beidseitige Druck des restlichen Stapels.`
        );
      }

      setZettelHinweis({
        art: warnungen.length > 0 ? "warnung" : "ok",
        text:
          warnungen.length > 0
            ? `${anzahl} Zettel erstellt. ${warnungen.join(" ")}`
            : a4
            ? `${anzahl} Zettel erstellt – ${Math.ceil(anzahl / 2)} A4-Bögen, vorn und hinten je zwei Teams.`
            : `${anzahl} Zettel erstellt – je zwei Seiten, für den beidseitigen Druck sortiert.`,
      });
    } catch (err) {
      console.error(err);
      setZettelHinweis({ art: "fehler", text: err.message || String(err) });
    } finally {
      setLoadingZettel(null);
    }
  };

  return (
    <div className="p-6 bg-secondary-950 text-white min-h-screen">
      <h1 className="text-2xl font-bold mb-6">Drucke & Exporte</h1>

      {/* === Team-Zettel fürs Klemmbrett === */}
      <div className="bg-secondary-900 border border-secondary-800 rounded-lg p-4 flex flex-col items-start mb-6">
        <div className="flex items-center gap-3 mb-2">
          <ClipboardList className="text-blue-400" />
          <h2 className="font-semibold text-lg">Team-Zettel (A5, Klemmbrett)</h2>
        </div>

        <p className="text-sm text-gray-400 mb-1">
          Ein Blatt A5 je Team: vorne die Spielliste zum Abhaken mit Platz für
          eigene Notizen und der QR-Code auf die persönliche Live-Ansicht, hinten
          der Lageplan mit denselben Stationsnummern.
        </p>
        <p className="text-xs text-gray-500 mb-4">
          Die Seiten liegen in der richtigen Reihenfolge für den beidseitigen
          Druck (Vorder- und Rückseite eines Teams direkt hintereinander). Damit
          die QR-Codes am Veranstaltungstag funktionieren, muss die Live-Ansicht
          unter „Freischalten“ freigeschaltet sein. Die Pins auf dem Lageplan
          werden unter „Lageplan“ gesetzt.
        </p>

        <div className="flex gap-2 mb-3" role="group" aria-label="Papierformat">
          {[
            { label: "A5 (ein Zettel je Blatt)", wert: "a5" },
            { label: "A4 (zwei Teams je Blatt)", wert: "a4" },
          ].map(({ label, wert }) => (
            <button
              key={wert}
              type="button"
              aria-pressed={zettelFormat === wert}
              onClick={() => setZettelFormat(wert)}
              className={`px-3 py-1.5 rounded text-sm border transition-colors ${
                zettelFormat === wert
                  ? "bg-primary-500 border-primary-500 text-black font-semibold"
                  : "border-secondary-700 text-gray-300 hover:bg-secondary-800"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {zettelFormat === "a4" && (
          <p className="text-xs text-gray-500 mb-3">
            A4 quer, auf <strong>beiden Seiten</strong> in 100 % drucken
            (Skalierung aus), Duplex <strong>„Wenden an der kurzen Kante“</strong>.
            Danach in der Mitte durchschneiden (kleine Striche markieren den Schnitt).
            Die Rückseiten stehen gespiegelt, damit hinter jedem Zettel sein eigener
            Lageplan liegt. Erst ein einzelnes Blatt probedrucken und gegen das Licht
            halten.
          </p>
        )}

        <div className="flex flex-wrap gap-2 w-full">
          {[
            { label: "Kinder", wert: "Kinder" },
            { label: "Erwachsene", wert: "Erwachsene" },
            { label: "Alle Teams", wert: null },
          ].map(({ label, wert }) => {
            const schluessel = wert || "alle";
            return (
              <button
                key={schluessel}
                disabled={loadingZettel !== null}
                onClick={() => downloadZettel(wert)}
                className="flex-1 min-w-[9rem] px-4 py-3 rounded bg-primary-500 hover:bg-primary-600 text-black font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
              >
                <Download size={18} />
                {loadingZettel === schluessel ? "wird erstellt…" : label}
              </button>
            );
          })}
        </div>

        {zettelHinweis && (
          <p
            className={`text-sm mt-3 ${
              zettelHinweis.art === "fehler"
                ? "text-red-400"
                : zettelHinweis.art === "warnung"
                ? "text-amber-400"
                : "text-green-400"
            }`}
          >
            {zettelHinweis.art === "fehler"
              ? "❌ "
              : zettelHinweis.art === "warnung"
              ? "⚠️ "
              : "✅ "}
            {zettelHinweis.text}
          </p>
        )}
      </div>

      {/* === Urkunden für die Kinder === */}
      <div className="bg-secondary-900 border border-secondary-800 rounded-lg p-4 flex flex-col items-start mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Award className="text-yellow-400" />
          <h2 className="font-semibold text-lg">Urkunden (Kinder)</h2>
        </div>

        <p className="text-sm text-gray-400 mb-1">
          Erstellt ein PDF mit einer A4-Urkunde je Kind – Teamname, Name des
          Kindes, Platzierung und Gesamtpunktzahl.
        </p>
        <p className="text-xs text-gray-500 mb-4">
          Sortiert nach Platzierung, bestes Team zuerst; innerhalb eines Teams in
          der Reihenfolge der eingetragenen Mitglieder. Bei Punktgleichheit
          entscheiden die Stationsplätze (mehr 1. Plätze, dann 2. Plätze …); nur
          wenn auch die gleich sind, wird der Platz geteilt. Zum Druck auf 300 g Papier gedacht – im Druckdialog
          „Tatsächliche Größe“ bzw. 100 % wählen, nicht „An Seite anpassen“, sonst
          entsteht ein weißer Rand um den Rahmen.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 w-full mb-4">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-gray-400">Ort</span>
            <input
              type="text"
              maxLength={60}
              value={urkunde.ort}
              onChange={(e) =>
                setUrkunde((bisher) => ({ ...bisher, ort: e.target.value }))
              }
              className="px-3 py-2 rounded bg-secondary-950 border border-secondary-700 text-white focus:outline-none focus:border-primary-500"
            />
          </label>

          <label className="flex flex-col gap-1 text-sm">
            <span className="text-gray-400">Datum</span>
            <input
              type="date"
              value={urkunde.datum}
              onChange={(e) =>
                setUrkunde((bisher) => ({ ...bisher, datum: e.target.value }))
              }
              className="px-3 py-2 rounded bg-secondary-950 border border-secondary-700 text-white focus:outline-none focus:border-primary-500"
            />
          </label>

          <label className="flex flex-col gap-1 text-sm">
            <span className="text-gray-400">Name über der Unterschrift</span>
            <input
              type="text"
              maxLength={40}
              placeholder="z. B. M. Beispiel"
              value={urkunde.unterschrift}
              onChange={(e) =>
                setUrkunde((bisher) => ({
                  ...bisher,
                  unterschrift: e.target.value,
                }))
              }
              className="px-3 py-2 rounded bg-secondary-950 border border-secondary-700 text-white placeholder-gray-600 focus:outline-none focus:border-primary-500"
            />
          </label>
        </div>

        {plaetzeFehlen && (
          <div className="w-full mb-4 p-3 rounded border border-red-700 bg-red-900/30 text-red-200 text-sm flex gap-2">
            <AlertTriangle className="w-5 h-5 shrink-0" />
            <span>
              Das Ranking enthält noch keine berechneten Plätze. Der Export bricht deshalb
              ab – bitte zuerst ein Ergebnis oder den Altersbonus erneut speichern.
            </span>
          </div>
        )}

        {gleichstaendeKinder.length > 0 && (
          <div className="w-full mb-4 p-3 rounded border border-yellow-600 bg-yellow-900/30 text-yellow-100 text-sm">
            <div className="flex items-center gap-2 font-semibold mb-2">
              <AlertTriangle className="w-5 h-5 shrink-0" />
              Punktgleiche Teams – bitte vor dem Druck prüfen
            </div>
            <ul className="space-y-2">
              {gleichstaendeKinder.map(({ team, offen, hinweise }) => (
                <li key={team.team_id}>
                  <span className={offen ? "text-red-300 font-semibold" : "font-semibold"}>
                    Platz {team.rank ?? "?"}: {team.team_name}
                    {offen ? " (Platz geteilt)" : ""}
                  </span>
                  <ul className="ml-4 text-xs text-yellow-200/90">
                    {(hinweise || []).map((h, i) => (
                      <li key={i}>• {h.text}</li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          </div>
        )}

        <button
          disabled={loadingUrkunden}
          onClick={downloadUrkunden}
          className="w-full px-4 py-3 rounded bg-primary-500 hover:bg-primary-600 text-black font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
        >
          <Download size={18} />
          {loadingUrkunden ? "Urkunden werden erstellt…" : "Urkunden-PDF erstellen"}
        </button>

        {urkundenHinweis && (
          <p
            className={`text-sm mt-3 ${
              urkundenHinweis.art === "fehler"
                ? "text-red-400"
                : urkundenHinweis.art === "warnung"
                ? "text-amber-400"
                : "text-green-400"
            }`}
          >
            {urkundenHinweis.art === "fehler"
              ? "❌ "
              : urkundenHinweis.art === "warnung"
              ? "⚠️ "
              : "✅ "}
            {urkundenHinweis.text}
          </p>
        )}
      </div>

      {/* === Grid Layout für Exporte === */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Stations-PDF */}
        <div className="bg-secondary-900 border border-secondary-800 rounded-lg p-4 flex flex-col items-start">
          <div className="flex items-center gap-3 mb-2">
            <FileText className="text-yellow-400" />
            <h2 className="font-semibold text-lg">Stationsunterlagen</h2>
          </div>
          <p className="text-sm text-gray-400 mb-4">
            Exportiert alle Stationen als PDF mit Spielbeschreibung und Regeln.
          </p>
          {dateiButton({
            pfad: "/pdf/export-stations",
            dateiname: "Kirtagames_stations.pdf",
            fehlertext: "Fehler beim Erstellen des Stations-PDFs",
            label: "Stations-PDF",
            className:
              "w-full px-4 py-2 rounded bg-primary-500 hover:bg-primary-600 text-black font-semibold transition-colors",
          })}
          {dateiMeldung("/pdf/export-stations")}
        </div>

        {/* Aushänge */}
        <div className="bg-secondary-900 border border-secondary-800 rounded-lg p-4 flex flex-col items-start">
          <div className="flex items-center gap-3 mb-2">
            <FileText className="text-green-400" />
            <h2 className="font-semibold text-lg">Stations-Aushänge</h2>
          </div>
          <p className="text-sm text-gray-400 mb-4">
            Erstellt Aushänge für jede Station zur Präsentation vor Ort.
          </p>
          {dateiButton({
            pfad: "/pdf/export-station-posters",
            dateiname: "Kirtagames_stations_posters.pdf",
            fehlertext: "Fehler beim Erstellen der Aushänge",
            label: "Aushänge-PDF",
            className:
              "w-full px-4 py-2 rounded bg-green-500 hover:bg-green-600 text-black font-semibold transition-colors",
          })}
          {dateiMeldung("/pdf/export-station-posters")}
        </div>

        {/* Ergebnisse */}
        <div className="bg-secondary-900 border border-secondary-800 rounded-lg p-4 flex flex-col items-start">
          <div className="flex items-center gap-3 mb-2">
            <FileText className="text-blue-400" />
            <h2 className="font-semibold text-lg">Ergebnislisten</h2>
          </div>
          <p className="text-sm text-gray-400 mb-4">
            Exportiert Gesamtergebnisse getrennt nach Kategorien.
          </p>
          <div className="flex flex-col gap-2 w-full">
            {dateiButton({
              pfad: "/pdf/export-teams-results-kinder",
              dateiname: "Kirtagames_ergebnisse_kinder.pdf",
              fehlertext: "Fehler beim Kinder-Ergebnisexport",
              label: "Kinder",
              className: "px-4 py-2 rounded bg-blue-500 hover:bg-blue-600 text-black font-semibold",
            })}
            {dateiButton({
              pfad: "/pdf/export-teams-results-erwachsene",
              dateiname: "Kirtagames_ergebnisse_erwachsene.pdf",
              fehlertext: "Fehler beim Erwachsenen-Ergebnisexport",
              label: "Erwachsene",
              className: "px-4 py-2 rounded bg-blue-700 hover:bg-blue-600 text-white font-semibold",
            })}
          </div>
          {dateiMeldung("/pdf/export-teams-results-kinder")}
          {dateiMeldung("/pdf/export-teams-results-erwachsene")}
        </div>
      </div>
    </div>
  );
}
